import { Fragment, ReactElement, ReactNode } from 'react';
import { Dialog as HeadlessDialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useInterruptibleTransition } from '../../hooks/useInterruptibleTransition';

interface DialogProps {
    open: boolean;
    onClose: () => void;
    title: string;
    children: ReactNode;
    size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
    /** Called once after the dialog is gone (also when a close skips the leave transition) - use this to clear state */
    afterLeave?: () => void;
    /** When false, disables closing via backdrop click and hides the X button. Default: true */
    dismissible?: boolean;
}

export function Dialog({ open, onClose, title, children, size = 'md', afterLeave, dismissible = true }: DialogProps): ReactElement {
    const { key, ...transitionProps } = useInterruptibleTransition(open, { afterLeave });

    const sizeClasses = {
        sm: 'max-w-sm sm:max-w-md',
        md: 'max-w-md sm:max-w-lg',
        lg: 'max-w-lg sm:max-w-xl',
        xl: 'max-w-xl sm:max-w-2xl',
        '2xl': 'max-w-2xl sm:max-w-3xl',
    };

    return (
        <Transition key={key} show={open} as={Fragment} {...transitionProps}>
            <HeadlessDialog as="div" className="relative z-50" onClose={dismissible ? onClose : () => {}}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/25 dark:bg-black/50" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <HeadlessDialog.Panel
                                className={`w-full ${sizeClasses[size]} transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl transition-all`}
                            >
                                <div className="flex items-center justify-between mb-4">
                                    <HeadlessDialog.Title
                                        as="h3"
                                        className="text-lg font-medium leading-6 text-gray-900 dark:text-gray-100"
                                    >
                                        {title}
                                    </HeadlessDialog.Title>
                                    {dismissible && (
                                        <button
                                            type="button"
                                            className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-500 dark:hover:bg-gray-700 dark:hover:text-gray-300"
                                            onClick={onClose}
                                            aria-label="Close"
                                        >
                                            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                                        </button>
                                    )}
                                </div>
                                <div>{children}</div>
                            </HeadlessDialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </HeadlessDialog>
        </Transition>
    );
}
